import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import heroVideo from "@/assets/hero-video.mp4";

interface Slider {
  id: string;
  title: string;
  subtitle: string | null;
  image_url: string;
  video_url: string | null;
  media_type: "image" | "video";
  link_url: string | null;
  link_text: string | null;
  display_order: number;
  is_active: boolean;
}

// Fallback slides if database is empty
const fallbackSlides = [
  {
    id: "fallback-1",
    title: "Japanese Aquarium Arts",
    subtitle: "A Beautiful Soul",
    image_url: "",
    video_url: heroVideo,
    media_type: "video" as const,
    link_url: "/shop",
    link_text: "Shop Now",
    display_order: 0,
    is_active: true,
  },
];

export function HeroSection() {
  const [slides, setSlides] = useState<Slider[]>(fallbackSlides);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    try {
      const { data, error } = await supabase
        .from("sliders")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        setSlides(data as Slider[]);
      }
    } catch (error) {
      console.error("Error fetching sliders:", error);
    } finally {
      setLoading(false);
    }
  };

  const startAutoPlay = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 8000);
  };

  useEffect(() => {
    if (isPlaying && slides.length > 1) {
      startAutoPlay();
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, slides.length]);

  useEffect(() => {
    if (videoRef.current) {
      const currentSlideData = slides[currentSlide];
      if (currentSlideData?.media_type === "video" && isVideoPlaying) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [currentSlide, isVideoPlaying, slides]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    if (isPlaying) startAutoPlay();
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    if (isPlaying) startAutoPlay();
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    if (isPlaying) startAutoPlay();
  };

  const toggleVideoPlay = () => {
    setIsVideoPlaying(!isVideoPlaying);
  };

  const slide = slides[currentSlide];

  if (!slide) return null;

  const getMediaSource = (s: Slider) => {
    if (s.media_type === "video") {
      return s.video_url || s.image_url;
    }
    return s.image_url;
  };

  return (
    <section className="relative h-screen min-h-[600px] flex items-center overflow-hidden w-full">
      {/* Background Slides */}
      <div className="absolute inset-0">
        {slides.map((s, index) => {
          const isActive = index === currentSlide;
          const isAdjacent = Math.abs(index - currentSlide) <= 1 || 
            (currentSlide === 0 && index === slides.length - 1) ||
            (currentSlide === slides.length - 1 && index === 0);
          return (
            <div
              key={s.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                isActive ? "opacity-100" : "opacity-0"
              }`}
            >
              {s.media_type === "video" ? (
                isAdjacent ? (
                  <video
                    ref={isActive ? videoRef : undefined}
                    className="absolute inset-0 w-full h-full object-cover"
                    src={getMediaSource(s)}
                    autoPlay={isActive}
                    muted
                    loop
                    playsInline
                    preload={isActive ? "auto" : "none"}
                    poster={s.image_url || undefined}
                  />
                ) : null
              ) : (
                isAdjacent ? (
                  <img
                    src={s.image_url}
                    alt={s.title}
                    className="absolute inset-0 w-full h-full object-cover scale-105"
                    loading={isActive ? "eager" : "lazy"}
                    fetchPriority={isActive ? "high" : "auto"}
                  />
                ) : null
              )}
            </div>
          );
        })}

        {/* Dark overlay - reduced for brighter video */}
        <div className="absolute inset-0 bg-[hsl(222,47%,11%)]/40" />
        
        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl ml-auto text-right">
          {/* Main Title */}
          <h1 
            className="font-playfair text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6 leading-[1.1] uppercase tracking-wide animate-fade-in"
          >
            <span className="text-primary">{slide.title}</span>
          </h1>

          {/* Subtitle */}
          {slide.subtitle && (
            <h2 
              className="text-primary font-playfair font-medium text-lg md:text-xl lg:text-2xl uppercase tracking-[0.15em] mb-6 animate-fade-in"
            >
              {slide.subtitle}
            </h2>
          )}

          {/* CTA Button */}
          {slide.link_url && slide.link_text && (
            <div 
              className="animate-fade-in"
            >
              <Link to={slide.link_url}>
                <Button 
                  size="lg" 
                  className="bg-primary text-primary-foreground hover:bg-primary/90 text-base md:text-lg px-10 py-6 uppercase tracking-wider font-medium"
                >
                  {slide.link_text}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Slider Controls - only show if multiple slides */}
      {slides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-4">
          {/* Slide indicators */}
          <div className="flex items-center gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-1 rounded-full transition-all duration-300 ${
                  index === currentSlide 
                    ? "w-8 bg-primary" 
                    : "w-2 bg-white/30 hover:bg-white/50"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Arrow Controls - only show if multiple slides */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full glass hover:bg-primary/20 transition-all"
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-6 w-6 text-white" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full glass hover:bg-primary/20 transition-all"
            aria-label="Next slide"
          >
            <ChevronRight className="h-6 w-6 text-white" />
          </button>
        </>
      )}

      {/* Video Play/Pause (only when video slide is active) */}
      {slide.media_type === "video" && (
        <button
          onClick={toggleVideoPlay}
          className="absolute bottom-8 right-8 z-20 p-3 rounded-full glass hover:bg-primary/20 transition-all"
          aria-label={isVideoPlaying ? "Pause video" : "Play video"}
        >
          {isVideoPlaying ? (
            <Pause className="h-5 w-5 text-white" />
          ) : (
            <Play className="h-5 w-5 text-white" />
          )}
        </button>
      )}
    </section>
  );
}
